import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../../../services/api';
import { EnderecoEntrega } from '../../../../types/enderecosEntrega';
import { Modal } from 'react-bootstrap';
import EnderecoEntregaForm from './EnderecoEntregaForm';

interface EnderecoEntregaListProps {
    opportunityId: number;
}

const EnderecoEntregaList: React.FC<EnderecoEntregaListProps> = ({ opportunityId }) => {
    const [enderecos, setEnderecos] = useState<EnderecoEntrega[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [selectedEnderecoId, setSelectedEnderecoId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedEndereco, setSelectedEndereco] = useState<EnderecoEntrega | null>(null);

    const fetchEnderecos = async () => {
        try {
            setLoading(true);
            const response = await apiService.getEnderecosEntregaByOportunidade(opportunityId);

            if (response.success) {
                setEnderecos(response.data);
                setError(null);
            } else {
                setError('Erro ao carregar endereços de entrega');
            }
        } catch (error) {
            console.error('Erro ao buscar endereços:', error);
            setError('Falha ao carregar endereços de entrega. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (opportunityId) {
            fetchEnderecos();
        }
    }, [opportunityId]);

    const handlePrepareEdit = (endereco: EnderecoEntrega) => {
        setSelectedEndereco(endereco);
        setShowEditModal(true);
    };

    const handlePrepareDelete = (endereco: EnderecoEntrega) => {
        setSelectedEnderecoId(endereco.id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!selectedEnderecoId) return;

        try {
            setIsDeleting(true);
            const response = await apiService.deleteEnderecoEntrega(selectedEnderecoId);

            if (response.success) {
                setEnderecos(enderecos.filter(e => e.id !== selectedEnderecoId));
                setShowDeleteModal(false);
                setSuccessMessage('Endereço de entrega excluído com sucesso!');
                setShowSuccessModal(true);

                // Fechar modal de sucesso após alguns segundos
                setTimeout(() => {
                    setShowSuccessModal(false);
                }, 2000);
            } else {
                setError('Erro ao excluir endereço');
            }
        } catch (error) {
            console.error('Erro ao excluir endereço:', error);
            setError('Falha ao excluir endereço. Por favor, tente novamente.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSaveEndereco = async () => {
        setShowAddModal(false);
        setShowEditModal(false);

        // Recarrega a lista de endereços
        await fetchEnderecos();

        // Exibe mensagem de sucesso
        setSuccessMessage('Endereço de entrega salvo com sucesso!');
        setShowSuccessModal(true);

        // Fechar modal de sucesso após alguns segundos
        setTimeout(() => {
            setShowSuccessModal(false);
        }, 2000);
    };

    const handleCancelForm = () => {
        setShowAddModal(false);
        setShowEditModal(false);
    };

    const formataCep = (cep?: string) => {
        if (!cep) return '';
        cep = cep.replace(/\D/g, '');
        if (cep.length === 8) {
            return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
        }
        return cep;
    };

    // Função para truncar texto longo (não sendo mais usada, mas mantida por compatibilidade)
    const truncateText = (text: string | null | undefined, maxLength: number = 100) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return `${text.substring(0, maxLength)}...`;
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between">
                <h4 className="fw-semibold">Endereços de Entrega</h4>
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAddModal(true)}
                >
                    <i className="ti ti-plus"></i> Novo Endereço
                </button>
            </div>
            <div className="card-body">
                {error && <div className="alert alert-danger">{error}</div>}

                {enderecos.length === 0 ? (
                    <div className="text-center p-4">
                        <div className="avatar avatar-lg bg-light-secondary rounded-circle mb-3">
                            <i className="ti ti-map-pin fs-24 text-secondary"></i>
                        </div>
                        <h5>Nenhum endereço de entrega cadastrado</h5>
                        <p className="text-muted">
                            Clique no botão "Novo Endereço" para adicionar um endereço de entrega para esta oportunidade.
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-nowrap mb-0">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: '120px' }}>Nome</th>
                                    <th style={{ minWidth: '200px' }}>Endereço</th>
                                    <th style={{ minWidth: '120px' }}>Responsável</th>
                                    <th style={{ minWidth: '80px' }}>Tipo</th>
                                    <th style={{ minWidth: '120px', maxWidth: '200px', width: '150px' }}>Observações</th>
                                    <th className="text-end" style={{ minWidth: '100px' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {enderecos.map((endereco) => (
                                    <tr key={endereco.id}>
                                        <td>
                                            <strong>{endereco.nome}</strong>
                                        </td>
                                        <td>
                                            {endereco.endereco}, {endereco.numero}
                                            {endereco.complemento && ` - ${endereco.complemento}`}
                                            <br />
                                            {endereco.bairro && `${endereco.bairro}, `}
                                            {endereco.municipio_nome} - {endereco.estado}
                                            {endereco.cep && <span><br />CEP: {formataCep(endereco.cep)}</span>}
                                        </td>
                                        <td>
                                            {endereco.contato_responsavel}
                                            {endereco.telefone_contato && <span><br />{endereco.telefone_contato}</span>}
                                        </td>
                                        <td>
                                            <span className="badge bg-info text-white">
                                                {endereco.tipo_endereco || 'Padrão'}
                                            </span>
                                        </td>
                                        <td style={{ 
                                            maxWidth: '200px', 
                                            width: '150px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {endereco.observacoes ? (
                                                <span 
                                                    title={endereco.observacoes}
                                                    style={{ 
                                                        cursor: 'help',
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {endereco.observacoes}
                                                </span>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end">
                                                <Link
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    to="#"
                                                    onClick={() => handlePrepareEdit(endereco)}
                                                    title="Editar endereço"
                                                >
                                                    <i className="ti ti-edit"></i>
                                                </Link>
                                                <Link
                                                    className="btn btn-sm btn-outline-danger"
                                                    to="#"
                                                    onClick={() => handlePrepareDelete(endereco)}
                                                    title="Excluir endereço"
                                                >
                                                    <i className="ti ti-trash"></i>
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal para adicionar endereço */}
            <Modal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Adicionar Endereço de Entrega</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <EnderecoEntregaForm
                        opportunityId={opportunityId}
                        onSave={handleSaveEndereco}
                        onCancel={handleCancelForm}
                    />
                </Modal.Body>
            </Modal>

            {/* Modal para editar endereço */}
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Editar Endereço de Entrega</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedEndereco && (
                        <EnderecoEntregaForm
                            opportunityId={opportunityId}
                            enderecoId={selectedEndereco.id}
                            endereco={selectedEndereco}
                            onSave={handleSaveEndereco}
                            onCancel={handleCancelForm}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal de confirmação de exclusão */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Body>
                    <div className="text-center">
                        <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                            <i className="ti ti-trash-x fs-36 text-danger" />
                        </div>
                        <h4 className="mb-2">Remover Endereço?</h4>
                        <p className="mb-0">
                            Tem certeza que deseja remover este endereço de entrega?
                            Esta ação não poderá ser desfeita.
                        </p>
                        <div className="d-flex align-items-center justify-content-center mt-4">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isDeleting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Excluindo...
                                    </>
                                ) : (
                                    'Sim, Excluir'
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal de sucesso */}
            <Modal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                centered
            >
                <Modal.Body>
                    <div className="success-message text-center">
                        <div className="success-popup-icon bg-success-light">
                            <i className="ti ti-check-check fs-36 text-success" />
                        </div>
                        <h3>Operação Concluída!</h3>
                        <p>{successMessage}</p>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default EnderecoEntregaList;